import { useEffect, useState } from "react";
import { MessageCircle, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import AuthModal from "@/components/auth/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { toolForm, toolParam } from "@/lib/webmcp/attrs";
import { T } from "@/i18n/T";
import {
  addSocialComment,
  deleteSocialComment,
  getSocialComments,
  type SocialComment,
} from "@/services/socialCommentService";
import { useT } from "@/i18n/useLocale";

interface Props {
  postId: string;
}

export function SocialPostComments({ postId }: Props) {
  const t = useT();
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState<SocialComment[]>([]);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getSocialComments(postId)
      .then((rows) => {
        if (active) setComments(rows);
      })
      .catch(() => {
        if (active) setComments([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [postId]);

  const submit = async () => {
    if (!user || !value.trim()) return;
    setSubmitting(true);
    try {
      const created = await addSocialComment(postId, user.id, value);
      setComments((prev) => [created, ...prev]);
      setValue("");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id: string) => {
    await deleteSocialComment(id);
    setComments((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <MessageCircle className="h-4 w-4" />
        Comments ({comments.length})
      </div>

      {isAuthenticated ? (
        <form
          className="space-y-2"
          {...toolForm("postSocialVideoComment", "Post a comment on the Byteline Social video the reader is watching.")}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label htmlFor="social-comment-text" className="sr-only">
            <T k="auto.your-comment-on-this-video-0b0a" />
          </label>
          <Textarea
            id="social-comment-text"
            name="comment"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("auto.add-a-comment-c3ed")}
            className="min-h-[72px] resize-none"
            {...toolParam("The comment text to post on this video.")}
          />
          <Button size="sm" type="submit" disabled={submitting || !value.trim()}>
            {submitting ? "Posting" : "Post"}
          </Button>
        </form>
      ) : (
        <>
          <Button size="sm" variant="secondary" className="w-full" onClick={() => setAuthOpen(true)}>
            <T k="auto.sign-in-to-comment-60a6" />
          </Button>
          <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
        </>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : comments.length > 0 ? (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              <ProfileAvatar
                userId={comment.user_id}
                avatarUrl={comment.profile?.avatar_url}
                name={comment.profile?.full_name}
                background={comment.profile?.avatar_background ?? undefined}
                size="sm"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">
                    {comment.profile?.full_name || comment.profile?.username || "Member"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm leading-relaxed">{comment.content}</p>
              </div>
              {user?.id === comment.user_id ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  aria-label={t("auto.delete-comment-547f")}
                  onClick={() => remove(comment.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground"><T k="auto.no-comments-yet-207b" /></p>
        )}
      </div>
    </div>
  );
}
